import React, { useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { COLORS } from "./constants";
import { primaryButtonSx, ghostButtonSx } from "./styles";

// ─────────────────────────────────────────────────────────────────────────────
// ProblemAlerts
//
// Why the last broadcast was refused or stopped, as the backend worded it
// (backend-/service/streamProblems.js, via /api/stream-status → problems).
//
// This is the "OBS says failed to connect and nothing else" case: YouTube
// refusing to create a broadcast, a stream key that expired, a destination that
// keeps dropping, an encoder that vanished. Each banner says what happened, why,
// and offers the one action that fixes it.
// ─────────────────────────────────────────────────────────────────────────────

const VISIBLE = 3;

const TONES = {
  error: {
    tone: COLORS.red, bg: COLORS.dangerSoft, line: "rgba(229,72,77,0.40)",
    tile: "rgba(229,72,77,0.18)", Icon: ErrorOutlineIcon
  },
  warning: {
    tone: COLORS.amber, bg: "rgba(244,183,64,0.08)", line: "rgba(244,183,64,0.40)",
    tile: "rgba(244,183,64,0.18)", Icon: WarningAmberIcon
  }
};

function ago(at) {
  const secs = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 60)} h ago`;
}

function Alert({ problem, onFix, onDismiss }) {
  const t = TONES[problem.severity] || TONES.error;
  const { Icon } = t;
  const fix = problem.fix;
  const meta = [
    problem.label,
    ago(problem.at),
    problem.count > 1 ? `${problem.count} times` : null
  ].filter(Boolean).join(" · ");

  return (
    <Box
      sx={{
        display: "flex", alignItems: { xs: "flex-start", sm: "center" }, gap: 2,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        p: { xs: "14px 14px 14px 16px", sm: "16px 14px 16px 20px" },
        borderRadius: "16px", bgcolor: t.bg, border: `1px solid ${t.line}`
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 40, height: 40, borderRadius: "11px", flex: "none",
          bgcolor: t.tile, color: t.tone,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: { xs: "calc(100% - 130px)", sm: 0 } }}>
        <Typography sx={{ color: COLORS.text, fontWeight: 700, fontSize: 16, lineHeight: 1.35 }}>
          {problem.title}
        </Typography>
        {problem.detail && (
          <Typography sx={{ mt: 0.4, color: COLORS.textSoft, fontSize: 14, lineHeight: 1.5 }}>
            {problem.detail}
          </Typography>
        )}
        <Typography sx={{ mt: 0.6, color: COLORS.muted, fontSize: 12 }}>{meta}</Typography>
      </Box>

      {/* On phones the button drops under the text, lined up with it. */}
      <Stack
        direction="row" spacing={0.5} alignItems="center"
        sx={{ flex: "none", ml: { xs: "56px", sm: 0 }, order: { xs: 3, sm: 0 } }}
      >
        {fix && (
          <Button
            size="small"
            onClick={() => onFix(problem)}
            endIcon={fix.type === "link" ? <OpenInNewIcon sx={{ fontSize: "16px !important" }} /> : null}
            sx={{
              ...(problem.severity === "error" ? primaryButtonSx : ghostButtonSx),
              px: 2, py: 0.9, whiteSpace: "nowrap"
            }}
          >
            {fix.label}
          </Button>
        )}
      </Stack>

      <Tooltip title="Dismiss">
        <IconButton
          aria-label={`Dismiss: ${problem.title}`}
          onClick={() => onDismiss(problem)}
          size="small"
          sx={{ flex: "none", color: COLORS.muted, ml: { xs: "auto", sm: 0 }, "&:hover": { color: COLORS.text } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function ProblemAlerts({ problems, onFix, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  if (!problems?.length) return null;

  const shown  = expanded ? problems : problems.slice(0, VISIBLE);
  const hidden = problems.length - shown.length;

  return (
    // Announces a NEW banner once. "additions" only, so the "2 min ago" text
    // ticking over on each 3 s poll is not read out again and again.
    <Stack spacing={1.25} aria-live="polite" aria-relevant="additions">
      {shown.map((p) => (
        <Alert key={p.id} problem={p} onFix={onFix} onDismiss={onDismiss} />
      ))}
      {(hidden > 0 || expanded) && problems.length > VISIBLE && (
        <Button
          size="small"
          onClick={() => setExpanded((v) => !v)}
          sx={{ alignSelf: "flex-start", textTransform: "none", color: COLORS.muted, fontWeight: 600 }}
        >
          {expanded ? "Show fewer" : `Show ${hidden} more`}
        </Button>
      )}
    </Stack>
  );
}
